const AXIS_LABELS = {
  warmth: 'Warmth',
  futurity: 'Futurity',
  chaos: 'Chaos',
  sacredness: 'Sacredness',
  organicness: 'Organicness',
  nostalgia: 'Nostalgia',
  surrealism: 'Surrealism',
  isolation: 'Isolation',
  emotionalSaturation: 'Emotional Saturation',
  corporateDensity: 'Corporate Density'
}

const CLUSTER_LINK_THRESHOLD = 0.82
const MAX_CLUSTER_LINK_DISTANCE = 165
const MIN_CLUSTER_SIZE = 2

export default class ClusterDetection {
  constructor() {
    this.clusters = []
    this.vibeToCluster = new Map()
    this.metrics = {
      clusterCount: 0
    }
  }

  update(vibes, links, genomeEngine) {
    const count = vibes.length
    const parents = new Uint16Array(count)
    const sizes = new Uint16Array(count)
    const idToIndex = new Map()

    for (let index = 0; index < count; index += 1) {
      parents[index] = index
      sizes[index] = 1
      idToIndex.set(vibes[index].id, index)
    }

    links.forEach(link => {
      if (link.strength < CLUSTER_LINK_THRESHOLD) {
        return
      }

      if (Math.hypot(link.a.x - link.b.x, link.a.y - link.b.y) > MAX_CLUSTER_LINK_DISTANCE) {
        return
      }

      const left = idToIndex.get(link.a.id)
      const right = idToIndex.get(link.b.id)

      if (left !== undefined && right !== undefined) {
        this.union(parents, sizes, left, right)
      }
    })

    const groups = new Map()

    for (let index = 0; index < count; index += 1) {
      const root = this.find(parents, index)
      const group = groups.get(root) ?? []
      group.push(vibes[index])
      groups.set(root, group)
    }

    this.vibeToCluster = new Map()

    this.clusters = [...groups.values()]
      .filter(members => members.length >= MIN_CLUSTER_SIZE)
      .map(members => this.createCluster(members, genomeEngine))
      .sort((left, right) => right.members.length - left.members.length)

    this.clusters.forEach(cluster => {
      cluster.members.forEach(vibe => {
        this.vibeToCluster.set(vibe.id, cluster.id)
      })
    })

    this.metrics.clusterCount = this.clusters.length
  }

  createCluster(members, genomeEngine) {
    const center = this.calculateCenter(members)
    const averageAxes = this.calculateAverageAxes(members)
    const dominantAxes = Object.entries(averageAxes)
      .sort((left, right) => right[1] - left[1])
      .slice(0, 3)
      .map(([axis, value]) => ({ axis, label: AXIS_LABELS[axis], value }))

    const weakestAxes = Object.entries(averageAxes)
      .sort((left, right) => left[1] - right[1])
      .slice(0, 2)
      .map(([axis, value]) => ({ axis, label: AXIS_LABELS[axis], value }))

    const radius = this.calculateRadius(members, center)
    const density = Math.min(1, members.length / Math.max(1, radius * 0.12))
    const cohesion = this.calculateCohesion(members, genomeEngine)
    const tension = 1 - cohesion
    const strongestVibes = this.findStrongestVibes(members, averageAxes, genomeEngine)
    const clusterId = `cluster-${members.map(member => member.id).sort()[0]}`

    return {
      id: clusterId,
      members,
      center,
      radius,
      density,
      cohesion,
      tension,
      averageAxes,
      dominantAxes,
      weakestAxes,
      strongestVibes,
      interactionRadius: Math.max(28, Math.min(54, radius * 0.2))
    }
  }

  calculateCenter(members) {
    const center = members.reduce((result, member) => {
      result.x += member.x
      result.y += member.y
      return result
    }, { x: 0, y: 0 })

    return {
      x: center.x / members.length,
      y: center.y / members.length
    }
  }

  calculateAverageAxes(members) {
    const totals = {}

    members.forEach(member => {
      Object.entries(member.axes).forEach(([axis, value]) => {
        totals[axis] = (totals[axis] || 0) + value
      })
    })

    Object.keys(totals).forEach(axis => {
      totals[axis] /= members.length
    })

    return totals
  }

  calculateRadius(members, center) {
    let maxDistance = 0

    members.forEach(member => {
      maxDistance = Math.max(maxDistance, Math.hypot(member.x - center.x, member.y - center.y))
    })

    return Math.max(64, maxDistance + 34)
  }

  calculateCohesion(members, genomeEngine) {
    if (members.length < 2) {
      return 1
    }

    let pairCount = 0
    let totalSimilarity = 0

    for (let i = 0; i < members.length; i += 1) {
      for (let j = i + 1; j < members.length; j += 1) {
        totalSimilarity += genomeEngine.calculateSimilarity(members[i].axes, members[j].axes)
        pairCount += 1
      }
    }

    return pairCount === 0 ? 1 : totalSimilarity / pairCount
  }

  findStrongestVibes(members, averageAxes, genomeEngine) {
    const syntheticCenter = { axes: averageAxes }

    return members
      .map(member => ({
        name: member.name,
        score: genomeEngine.calculateSimilarity(member.axes, syntheticCenter.axes)
      }))
      .sort((left, right) => right.score - left.score)
      .slice(0, 3)
      .map(entry => entry.name)
  }

  getClusters() {
    return this.clusters
  }

  getMetrics() {
    return this.metrics
  }

  getClusterIdForVibe(vibeId) {
    return this.vibeToCluster.get(vibeId) ?? null
  }

  getClusterAssignments() {
    return this.vibeToCluster
  }

  find(parents, index) {
    let current = index

    while (parents[current] !== current) {
      parents[current] = parents[parents[current]]
      current = parents[current]
    }

    return current
  }

  union(parents, sizes, left, right) {
    const rootLeft = this.find(parents, left)
    const rootRight = this.find(parents, right)

    if (rootLeft === rootRight) {
      return
    }

    if (sizes[rootLeft] < sizes[rootRight]) {
      parents[rootLeft] = rootRight
      sizes[rootRight] += sizes[rootLeft]
      return
    }

    parents[rootRight] = rootLeft
    sizes[rootLeft] += sizes[rootRight]
  }
}
